import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

/**
 * /applications/:applicationId — the landing spot for links from outside.
 *
 * The payments console knows a candidate only by the applicationId that was
 * on the Jodo sheet. This route turns that id into wherever the candidate
 * actually lives — the job pipeline they applied to — so a payment row is one
 * click from the person behind it instead of a search.
 *
 * Deliberately a redirect, not a page: candidates are worked on inside their
 * job's pipeline, and a second "profile page" would immediately drift from
 * the first. A pooled application (no job yet) lands on the talent pool.
 */
export default function ApplicationLocate() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/resumes/locate/by-application/${encodeURIComponent(applicationId)}`);
        if (cancelled) return;
        const { jobId } = res.data?.data || {};
        // replace: the redirect should not be a back-button stop.
        navigate(jobId ? `/jobs/${jobId}` : '/dashboard/talent-pool', { replace: true });
      } catch (e) {
        if (cancelled) return;
        setProblem(e.response?.status === 404
          ? `No application "${applicationId}" exists here. If it was on a payment, the sheet row may predate the application.`
          : 'Could not look that application up. Try again, or search for the candidate by name.');
      }
    })();
    return () => { cancelled = true; };
  }, [applicationId, navigate]);

  return (
    <div style={{ padding: '4rem', textAlign: 'center', fontFamily: 'inherit' }}>
      {problem
        ? <>
            <h3>Application not found</h3>
            <p style={{ color: '#666', maxWidth: '32rem', margin: '1rem auto' }}>{problem}</p>
          </>
        : <p style={{ color: '#666' }}>Finding {applicationId}…</p>}
    </div>
  );
}
